import type { Work, WorksSortKey } from '~/composables/api/useWorkApi'

/**
 * #803: 作品一覧の「タブ + 取得 + 積み増し」を 1 箇所に集約する。
 *
 * トップ / `/arcanelab` / `/c/<slug>` / `/cradle` の 4 面で別々に手書きされていた
 * ロード制御（loading フラグ・stale レスポンスのガード・ページング）をここへ寄せる。
 * **並べる器（グリッド / カルーセル）の CSS は各面に残す** —— centering43 の中は
 * `calc(N * var(--u))`、landing は `dvw` という使い分け（`responsive_sizing.md`）が
 * あるので、1 つのグリッドに統一すると逆にレイアウト規約を壊す。
 */

/** タブ 1 つぶんの定義。トップと `/arcanelab` で二重に持っていたものを共通化する。 */
export interface WorkGalleryTab {
  key: WorksSortKey
  label: string
}

export interface WorkGalleryPage {
  works: Work[]
  total: number
}

/** ページを 1 枚取ってくる関数。3 面とも `{works, total}` の同じ形に揃えてある。 */
export type WorkGalleryFetcher = (
  page: number,
  perPage: number,
  sort: WorksSortKey,
) => Promise<WorkGalleryPage>

export interface UseWorkGalleryOptions {
  fetcher: WorkGalleryFetcher
  perPage: number
  initialSort?: WorksSortKey
  /**
   * 親がすでに 1 ページ目を持っている場合に渡す（`/c/<slug>` の SSR 同梱ぶん）。
   * これがあると初回の fetch を省き、2 ページ目から取りに行く。
   */
  initialItems?: Work[]
  initialTotal?: number
  /**
   * `append` = スクロール末端で次ページを積み増す。
   * `fixed`  = 1 ページで打ち止め（トップページの 6 件固定）。
   */
  mode?: 'append' | 'fixed'
}

export function useWorkGallery(options: UseWorkGalleryOptions) {
  const { fetcher, perPage, mode = 'append' } = options

  const works = ref<Work[]>([...(options.initialItems ?? [])])
  const total = ref<number>(options.initialTotal ?? works.value.length)
  const sort = ref<WorksSortKey>(options.initialSort ?? 'new')
  const page = ref<number>(works.value.length > 0 ? 1 : 0)

  // `initialItems` を使う面（`/c` の SSR 同梱ぶん）では、**初回ページの件数と
  // `perPage` が一致していなければならない**。ズレると「次は page 2」の指す範囲が
  // SSR 済みの範囲と食い違い、作品を静かに取りこぼす。開発時だけ気づけるようにする。
  if (import.meta.dev && options.initialItems && options.initialItems.length > 0) {
    const n = options.initialItems.length
    if (n !== perPage && n < (options.initialTotal ?? n)) {
      console.warn(
        `[useWorkGallery] initialItems (${n} 件) と perPage (${perPage}) が不一致です。`
        + ' 追記時にページ境界がずれて作品を取りこぼします。',
      )
    }
  }

  /** 初回ロード中（＝まだ 1 件も出せていない）。スケルトン表示の判定に使う。 */
  const loading = ref<boolean>(false)
  /** 2 ページ目以降を取得中。末尾のスピナー表示に使う。 */
  const loadingMore = ref<boolean>(false)

  /** サーバが空ページを返した = 末尾を越えた。`total` が古かった場合の保険。 */
  const exhausted = ref<boolean>(false)

  /**
   * 直近の取得が失敗したときのエラー。**セットされている間は自動ロードを止める**。
   * 止めないと、センチネルが交差したままなので失敗するリクエストを撃ち続ける。
   */
  const error = ref<unknown>(null)

  /**
   * stale レスポンスのガード。タブを素早く切り替えると、前のタブのレスポンスが
   * 後から届いて一覧が混ざる。append 方式では「消えたはずの作品が積まれる」形で
   * 表面化するので、ページ送り時代より重要になっている。
   */
  let fetchSeq = 0

  /**
   * 残りがあるかは **ページ番号で数える**（`works.length` と `total` の比較ではない）。
   *
   * `WorksAccessService` は resilient モードで壊れた作品を黙って落とし、
   * `appendUnique` も重複を落とすので、`works.length` は `total` に届かないことがある。
   * それを「まだ残っている」と読むと末尾で空ページを永久に取りに行き（無限リクエスト）、
   * 逆に「短いページが来たら終わり」と読むと**途中で 1 件落ちただけで残りを全部
   * 打ち切る**（実際に E2E で踏んだ）。offset ページングの残りは純粋に
   * 「何ページ目まで読んだか」で決まる。
   */
  const totalPages = computed<number>(() => Math.ceil(total.value / perPage))

  const hasMore = computed<boolean>(
    () => mode === 'append'
      && !exhausted.value
      && !error.value
      && page.value < totalPages.value,
  )

  const isEmpty = computed<boolean>(() => !loading.value && works.value.length === 0)

  /**
   * 既出の workId を落として積む。
   *
   * `popular` は `_likes` をリクエストごとに再計算するため、スクロール中にいいねが
   * 動くとページ境界がずれて同じ作品が 2 度返る。`featured` も母集団が育つと
   * リクエストごとのシャッフルで同じことが起きる（#802）。サーバ側の対策を待たずに
   * ここで潰しておけば、着手順に依存しない。
   */
  const appendUnique = (incoming: Work[]): void => {
    const seen = new Set(works.value.map(w => w.workId))
    for (const w of incoming) {
      if (seen.has(w.workId)) continue
      seen.add(w.workId)
      works.value.push(w)
    }
  }

  const fetchPage = async (nextPage: number, { append }: { append: boolean }): Promise<void> => {
    const seq = ++fetchSeq
    if (append) loadingMore.value = true
    else loading.value = true

    try {
      const resp = await fetcher(nextPage, perPage, sort.value)
      if (seq !== fetchSeq) return // 新しいリクエストが出ている＝この結果は捨てる

      total.value = resp.total
      if (append) appendUnique(resp.works)
      else works.value = [...resp.works]
      page.value = nextPage

      // 空ページ = 末尾を越えた。`total` が実態より大きいまま（削除直後など）でも
      // ここで必ず止まる。
      if (resp.works.length === 0) exhausted.value = true
    }
    catch (e) {
      if (seq !== fetchSeq) return
      // 握り潰さない。`error` を立てて自動ロードを止め、再試行はユーザー操作に委ねる。
      // 止めないとセンチネルが交差したままなので、失敗するリクエストを撃ち続ける。
      console.error('[useWorkGallery] 作品一覧の取得に失敗しました', e)
      error.value = e
    }
    finally {
      if (seq === fetchSeq) {
        loading.value = false
        loadingMore.value = false
      }
    }
  }

  /** 初回ロード。`initialItems` を受け取っている場合は何もしない。 */
  const load = async (): Promise<void> => {
    if (works.value.length > 0) return
    await fetchPage(1, { append: false })
  }

  /** スクロール末端から呼ばれる。多重発火とページ末尾での空打ちを弾く。 */
  const loadMore = async (): Promise<void> => {
    if (!hasMore.value || loading.value || loadingMore.value) return
    await fetchPage(page.value + 1, { append: true })
  }

  /**
   * タブ切替。積み上げたぶんを捨てて 1 ページ目から取り直す。
   *
   * **切替中に `works` を空にしない**。空にすると「読み込み中（0 件）」の分岐へ落ちて
   * 一覧が一瞬まるごと消える —— トップページではタブ自体が default slot の中にあるので
   * カーソルの下からタブが消え、`/arcanelab` には `#loading` slot が無いので真っ白になる。
   * ページ送りの頃と同じく、**新しいページが届いてから差し替える**（`fetchPage` の
   * `append: false` が担当）。取得中は `loading` が立つので `loadMore` も走らない。
   */
  const changeSort = async (next: WorksSortKey): Promise<void> => {
    if (next === sort.value) return
    sort.value = next
    page.value = 0
    exhausted.value = false
    error.value = null
    await fetchPage(1, { append: false })
  }

  /** 現在のタブを取り直す（作品の削除・公開状態の変更後など）。 */
  const reload = async (): Promise<void> => {
    page.value = 0
    exhausted.value = false
    error.value = null
    await fetchPage(1, { append: false })
  }

  /** 失敗したページを取り直す。エラー表示の「再試行」から呼ぶ。 */
  const retry = async (): Promise<void> => {
    if (!error.value) return
    error.value = null
    await fetchPage(page.value + 1, { append: page.value > 0 })
  }

  return {
    works,
    total,
    sort,
    page,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    error,
    load,
    loadMore,
    changeSort,
    reload,
    retry,
  }
}
